import { useDispatch, useSelector } from 'react-redux';
import { getTextProcessStatus, inspection, getInspectionSettings } from 'store';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { useState, useEffect } from 'react';

import styles from './FontSizeSettings.module.css';

const FontSizeSettings = () => {
  const dispatch = useDispatch();
  const documentProcessed = useSelector(getTextProcessStatus);
  const inspectionSettings = useSelector(getInspectionSettings);

  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    const value = inspectionSettings.fontSizeCheckValue;
    if (value === undefined || value === null) return '6';
    return value % 1 === 0 ? value.toString() : value.toFixed(1);
  });
  const [warning, setWarning] = useState('');

  // Format number to show decimal only when needed
  const formatNumber = (num: number): string => {
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  // sync Redux state with local state
  useEffect(() => {
    if (
      inspectionSettings.fontSizeCheckValue !== undefined &&
      inspectionSettings.fontSizeCheckValue !== null &&
      !isEditing
    ) {
      setInputValue(formatNumber(inspectionSettings.fontSizeCheckValue));
    }
  }, [inspectionSettings.fontSizeCheckValue, isEditing]);

  const isEnabled =
    Boolean(inspectionSettings.fontSizeCheckValue !== null && inspectionSettings.fontSizeCheckValue !== undefined) ||
    isEditing;

  const toggleFontSizeCheck = () => {
    if (isEnabled) {
      // Disable checkbox and clear the value
      dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: null }));
      setInputValue('6');
      setIsEditing(false);
    } else {
      // Enable checkbox with default value
      dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: 6 }));
      setInputValue('6');
    }
  };

  const handleNumberInputChange = (value: string | null) => {
    const nextValue = value ?? '';
    // Allow only numbers with up to 1 decimal point
    const regex = /^\d*\.?\d{0,1}$/;
    if (regex.test(nextValue) || nextValue === '') {
      setInputValue(nextValue);
      setIsEditing(true);

      // Only update Redux if we have a valid number
      if (nextValue !== '' && nextValue !== '.') {
        const numValue = parseFloat(nextValue);
        if (!isNaN(numValue)) {
          dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: numValue }));
        }
      }

      if (nextValue !== '' && parseFloat(nextValue) < 4) {
        setWarning('Minimum must be at least 4pt');
      } else {
        setWarning('');
      }
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (inputValue === '' || inputValue === '.') {
      setInputValue('6');
      dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: 6 }));
    } else if (inputValue.endsWith('.')) {
      const numValue = parseFloat(inputValue + '0');
      setInputValue(formatNumber(numValue));
      dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: numValue }));
    } else if (inputValue.startsWith('.')) {
      const numValue = parseFloat('0' + inputValue);
      setInputValue(formatNumber(numValue));
      dispatch(inspection.actions.setInspectionSettings({ fontSizeCheckValue: numValue }));
    } else {
      const numValue = parseFloat(inputValue);
      setInputValue(formatNumber(numValue));
    }
  };

  return (
    <div className={styles.root}>
      <GVCheckbox
        checked={isEnabled}
        onChange={toggleFontSizeCheck}
        label="Font Size Check"
        data-testid="fontsize-checkbox"
        disabled={!documentProcessed}
      />
      {isEnabled && (
        <div className={styles.control}>
          Minimum&nbsp;
          <GVTextField
            label="Font size"
            isLabelHidden
            textAlign="center"
            autoComplete="off"
            value={inputValue}
            onValueChange={handleNumberInputChange}
            onBlur={handleBlur}
            data-testid="fontsize-input"
            hasError={Boolean(warning)}
            message={warning || undefined}
          />
          &nbsp;pts
        </div>
      )}
    </div>
  );
};

export default FontSizeSettings;
